// Broker-side new-hire / recently-eligible helpers used by the Client Detail
// Benefits Overview banner. Wraps the canonical shared `calculateEligibility`
// so this surface respects the actual plan settings (waitingPeriod /
// waitingPeriodType / entryDate) instead of the old hardcoded "30 days +
// first of following month" math.

import {
  calculateEligibility,
  type EntryDateType,
  type WaitingPeriodType,
} from "@shared/utils/eligibilityCalculator";
import { parseDateLocal } from "@/lib/utils";

export interface EnrollmentDates {
  hireDate: Date;
  eligibilityDate: Date;
  effectiveDate: Date;
  enrollmentDueDate: Date;
}

export interface NewHireInfo extends EnrollmentDates {
  /** Days left in the 30-day initial enrollment window. */
  daysRemaining: number;
  /** Days until the enrollment due date (negative once it has passed). */
  daysUntilDue: number;
  isInEnrollmentPeriod: boolean;
}

export interface EligibilityChangeInfo extends EnrollmentDates {
  daysUntilDue: number;
  isRecentlyEligible: boolean;
}

export interface EnrollmentConfig {
  /** Waiting period from the plan (defaults to 0 — immediate eligibility). */
  waitingPeriodDays?: number;
  /** Unit for waiting period; defaults to "days". */
  waitingPeriodType?: WaitingPeriodType;
  /** Entry-date rule from the plan; defaults to "first_of_following_month". */
  entryDate?: EntryDateType;
  /** Days before effective date that enrollment must be completed. */
  enrollmentDueOffsetDays?: number;
}

/** ACA initial enrollment window — used only to decide "is this employee
 * still in their initial enrollment period?", not for the actual eligibility
 * math. */
export const INITIAL_ENROLLMENT_PERIOD_DAYS = 30;
export const DEFAULT_ENROLLMENT_DUE_OFFSET_DAYS = 7;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Local midnight of the calendar day `d` falls on (local getters). */
function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** "YYYY-MM-DD" for the local calendar day `d` falls on. Never goes through
 * toISOString(), which reports the UTC day. */
export function toDateOnlyString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Whole calendar days from `from` to `to`, by local calendar day. Rounded so
 * the 23/25-hour day around a DST change still counts as one day. */
function calendarDaysBetween(from: Date, to: Date): number {
  return Math.round(
    (startOfLocalDay(to).getTime() - startOfLocalDay(from).getTime()) / MS_PER_DAY,
  );
}

/**
 * Compute eligibility / effective / enrollment-due dates for a single hire
 * date. Delegates to the canonical `calculateEligibility` so the math respects
 * the plan's waitingPeriod + entryDate rules instead of hardcoding 30 days.
 *
 * The shared calculator works on UTC-noon timestamps and reads a Date input
 * with UTC getters. Every date on this surface is a calendar day, so the hire
 * date goes in as "YYYY-MM-DD" (its local calendar day) and the results come
 * back out as local midnight. That way neither the math nor `formatDateSafe`
 * (local getters) can land on the previous/next day in any time zone.
 */
export function calculateEnrollmentDates(
  hireDate: Date,
  config: EnrollmentConfig = {},
): EnrollmentDates {
  const result = calculateEligibility({
    hireDate: toDateOnlyString(hireDate),
    waitingPeriod: config.waitingPeriodDays ?? 0,
    waitingPeriodType: config.waitingPeriodType ?? "days",
    entryDate: config.entryDate ?? "first_of_following_month",
    // This banner is plan-agnostic — we don't gate on employment-type
    // eligibility here. Use permissive defaults so the calculator returns
    // the dates even if the employee row is missing employmentType / hours.
    employmentType: "full-time",
    planEmploymentTypes: ["full-time"],
    enrollmentBufferDays:
      config.enrollmentDueOffsetDays ?? DEFAULT_ENROLLMENT_DUE_OFFSET_DAYS,
  });

  // The calculator's dates are UTC noon: the calendar day is its UTC day.
  const utcDayToLocal = (d: Date): Date =>
    new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

  // calculateEligibility returns null dates if it bails (bad input). Fall back
  // to the hire date so the UI never crashes on a malformed row.
  const hireLocal = startOfLocalDay(hireDate);
  const eligibilityDate = result.eligibilityDate ? utcDayToLocal(result.eligibilityDate) : hireLocal;
  const effectiveDate = result.effectiveDate ? utcDayToLocal(result.effectiveDate) : hireLocal;
  const enrollmentDueDate = result.enrollmentDueDate ? utcDayToLocal(result.enrollmentDueDate) : hireLocal;

  return {
    hireDate,
    eligibilityDate,
    effectiveDate,
    enrollmentDueDate,
  };
}

export function getNewHireInfo(
  hireDate: Date,
  config: EnrollmentConfig = {},
): NewHireInfo {
  const today = startOfLocalDay(new Date());
  const hireDateNorm = startOfLocalDay(hireDate);

  const daysSinceHire = calendarDaysBetween(hireDateNorm, today);

  const daysRemaining = Math.max(0, INITIAL_ENROLLMENT_PERIOD_DAYS - daysSinceHire);
  const isInEnrollmentPeriod =
    daysSinceHire >= 0 && daysSinceHire <= INITIAL_ENROLLMENT_PERIOD_DAYS;

  const dates = calculateEnrollmentDates(hireDateNorm, config);
  // Whole days from today to the Enrollment Due date (0 or less = overdue).
  const daysUntilDue = calendarDaysBetween(today, dates.enrollmentDueDate);

  return {
    ...dates,
    daysRemaining,
    daysUntilDue,
    isInEnrollmentPeriod,
  };
}

export function getEligibilityChangeInfo(
  hireDate: Date,
  config: EnrollmentConfig = {},
): EligibilityChangeInfo {
  const today = startOfLocalDay(new Date());

  const dates = calculateEnrollmentDates(startOfLocalDay(hireDate), config);

  const eligibilityNorm = startOfLocalDay(dates.eligibilityDate);

  // Employee shows as recently eligible from eligibility date until end of the
  // effective month.
  const endOfEffectiveMonth = new Date(dates.effectiveDate);
  endOfEffectiveMonth.setMonth(endOfEffectiveMonth.getMonth() + 1);
  endOfEffectiveMonth.setDate(0);
  endOfEffectiveMonth.setHours(23, 59, 59, 999);

  const isRecentlyEligible = eligibilityNorm <= today && today <= endOfEffectiveMonth;

  const daysUntilDue = calendarDaysBetween(today, dates.enrollmentDueDate);

  return {
    ...dates,
    daysUntilDue,
    isRecentlyEligible,
  };
}

// Hire dates arrive as "YYYY-MM-DD". `new Date("YYYY-MM-DD")` is UTC midnight,
// which is the previous evening anywhere west of Greenwich — so a 10/1 hire
// produced 9/30 eligibility, effective and due dates. Parse as a calendar date.
function hireDateOf(emp: { hireDate?: string | Date | null }): Date | null {
  return emp.hireDate ? parseDateLocal(emp.hireDate) : null;
}

export function filterNewHires<T extends { hireDate?: string | Date | null }>(
  employees: T[],
  config: EnrollmentConfig = {},
): (T & { enrollmentInfo: NewHireInfo })[] {
  return employees
    .map((emp) => ({ emp, hireDate: hireDateOf(emp) }))
    .filter(({ hireDate }) => hireDate != null && getNewHireInfo(hireDate, config).isInEnrollmentPeriod)
    .map(({ emp, hireDate }) => ({
      ...emp,
      enrollmentInfo: getNewHireInfo(hireDate!, config),
    }));
}

export function filterRecentlyEligible<T extends { hireDate?: string | Date | null }>(
  employees: T[],
  config: EnrollmentConfig = {},
): (T & { eligibilityInfo: EligibilityChangeInfo })[] {
  return employees
    .map((emp) => ({ emp, hireDate: hireDateOf(emp) }))
    .filter(({ hireDate }) => hireDate != null && getEligibilityChangeInfo(hireDate, config).isRecentlyEligible)
    .map(({ emp, hireDate }) => ({
      ...emp,
      eligibilityInfo: getEligibilityChangeInfo(hireDate!, config),
    }));
}

// ---------------------------------------------------------------------------
// Which rule produced the dates. The page shows this next to the New Hires
// table so a broker can tell "immediate / 0 days" from "no settings found".
// ---------------------------------------------------------------------------

export interface EnrollmentRule {
  config: EnrollmentConfig;
  /** "plan": taken from the client's eligibility settings; "default": the
   * settings call returned nothing, so the dates come from the fallback. */
  source: "plan" | "default";
  /** The plan whose settings were used (source === "plan"). */
  planId?: number;
}

/** Row shape of GET /api/client-plan-eligibility?clientId=… (no planId). */
export interface ClientPlanEligibilitySetting {
  planId?: number | null;
  waitingPeriod?: number | string | null;
  waitingPeriodType?: string | null;
  entryDate?: string | null;
}

/**
 * Pick the rule the New Hires / Recently Eligible tables use. The most
 * permissive plan wins (smallest waiting period after normalizing months →
 * days): the broker wants to know "when CAN this employee start enrolling
 * somewhere?". With no settings at all, fall back to immediate / 0 days so
 * the table still shows meaningful dates instead of arbitrary 30-day math.
 */
export function resolveEnrollmentRule(
  settings: ClientPlanEligibilitySetting[] | null | undefined,
): EnrollmentRule {
  if (!Array.isArray(settings) || settings.length === 0) {
    return {
      config: { waitingPeriodDays: 0, waitingPeriodType: "days", entryDate: "immediate" },
      source: "default",
    };
  }
  const normalize = (s: ClientPlanEligibilitySetting) => {
    const wp = Number(s?.waitingPeriod) || 0;
    const wpType: WaitingPeriodType = s?.waitingPeriodType === "months" ? "months" : "days";
    // Rough days-equivalent so months and days are comparable.
    const wpDays = wpType === "months" ? wp * 30 : wp;
    return { wpDays, raw: s };
  };
  const mostPermissive = settings.map(normalize).sort((a, b) => a.wpDays - b.wpDays)[0];
  const s = mostPermissive.raw;
  return {
    config: {
      waitingPeriodDays: Number(s?.waitingPeriod) || 0,
      waitingPeriodType: s?.waitingPeriodType === "months" ? "months" : "days",
      // DB values may carry dashes ("first-of-following-month").
      entryDate: (String(s?.entryDate || "first_of_following_month").replace(/-/g, "_")) as EntryDateType,
    },
    source: "plan",
    planId: s?.planId ?? undefined,
  };
}

const ENTRY_DATE_LABELS: Record<string, string> = {
  immediate: "immediate",
  first_of_month: "first of month",
  first_of_following_month: "first of following month",
  "15th_of_month": "15th of month",
};

/** Human-readable rule, e.g. "Waiting period: none · Entry: immediate" or
 * "Default rule (no eligibility settings found)". `planName` is appended when
 * known so the broker can see which plan's settings won. */
export function describeEnrollmentRule(rule: EnrollmentRule, planName?: string | null): string {
  if (rule.source === "default") return "Default rule (no eligibility settings found)";
  const { waitingPeriodDays = 0, waitingPeriodType = "days", entryDate = "first_of_following_month" } = rule.config;
  const unit = waitingPeriodType === "months" ? "month" : "day";
  const waiting =
    waitingPeriodDays <= 0 ? "none" : `${waitingPeriodDays} ${unit}${waitingPeriodDays === 1 ? "" : "s"}`;
  const entryKey = String(entryDate).replace(/-/g, "_");
  const entry = ENTRY_DATE_LABELS[entryKey] ?? entryKey.replace(/_/g, " ");
  const plan = planName ? ` · Plan: ${planName}` : "";
  return `Waiting period: ${waiting} · Entry: ${entry}${plan}`;
}

export function formatDaysRemaining(days: number): string {
  if (days === 0) return "Today!";
  if (days === 1) return "1 day";
  return `${days} days`;
}

export function formatDaysUntilDue(days: number): string {
  if (days <= 0) return "Overdue";
  if (days === 1) return "1 day";
  return `${days} days`;
}
